import React, { useState, useCallback, useEffect } from 'react';
import {
    Grid,
    Box,
    Typography,
    Avatar,
    Checkbox,
    Card,
    CardActions,
    CardActionArea,
    CardMedia,
    IconButton,
    Container,
} from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import DeleteIcon from '@mui/icons-material/Delete';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@mui/styles';
import { useDropzone } from 'react-dropzone';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import EmailEchos from './echos/emailEchos';
import { NoImage, Upload } from '../../../../assets';
import {
    getAllCampaigns,
    getCampaignDetails,
    uploadImage,
    deleteImage,
} from '../../../../redux/thunks/campaignThunks';
import {
    createMessage,
    updateMessage,
} from '../../../../redux/thunks/emailCampaignThunk';
import { CustomEditor, CustomButton, ConfirmationModal } from '../../../shared';

const useStyles = makeStyles((theme) => ({
    root: {
        '&$checked': {
            color: '#8C3F7D',
        },
    },
    checked: {},
}));

const emailSchema = yup
    .object({
        subject: yup.string().required('Subject is required'),
        emailBody: yup.string().required('Email Body is required'),
        isChecked: yup.boolean().default(false),
    })
    .required();

function EmailCampaign(props) {
    const { campaignId, handleClose, handleUpdateCampaign } = props;
    const [showModal, setShowModal] = useState(false);
    const [emailBanner, setEmailBanner] = useState(null);
    const dispatch = useDispatch();
    const { company } = useSelector((state) => state.company);
    const { campaign } = useSelector((state) => state.campaign);
    const classes = useStyles();
    const {
        register,
        handleSubmit,
        setValue,
        getValues,
        formState: { errors, isValid },
        watch,
    } = useForm({
        resolver: yupResolver(emailSchema),
    });

    const onDrop = useCallback(
        (acceptedFiles) => {
            const file = acceptedFiles[0];
            setEmailBanner(file);
        },
        [setValue]
    );

    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        accept: {
            'image/png': ['.png'],
            'image/jpeg': ['.jpg', '.jpeg'],
            
        },
    });

    const onFileSelect = (file) => {
        setEmailBanner(file);
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        onFileSelect(file);
    };

    const onSubmit = (data) => {
        if (data.isChecked) {
            let emailCampaignData = {
                subject: data.subject,
                message: data.emailBody,
            };
            if (campaign.message === null || campaign.message?.id === null) {
                dispatch(
                    createMessage({
                        companyId: company.id,
                        campaignId: campaignId,
                        data: emailCampaignData,
                    })
                ).then(()=> {
                    dispatch(
                        getCampaignDetails({
                            companyId: company.id,
                            campaignId: campaignId,
                        })
                    );
                }).catch(error => console.error("Error in Creating Message for Email Campaign", error));
            } else {
                dispatch(
                    updateMessage({
                        companyId: company.id,
                        campaignId: campaignId,
                        data: emailCampaignData,
                        messageId: campaign.message.id,
                    })
                ).then(()=> {
                    dispatch(
                        getCampaignDetails({
                            companyId: company.id,
                            campaignId: campaignId,
                        })
                    );
                }).catch(error => console.error("Error in Updating Message for Email Campaign", error));
            }
        } else {
            setShowModal(!showModal);
        }
    };

    const handleEmailBody = (content) => {
        setValue('emailBody', content);
    };

    const handleSave = () => {
        handleSubmit(onSubmit);
    };

    const handleDeleteImage = (id) => {
        dispatch(
            deleteImage({
                companyId: company.id,
                campaignId: campaignId,
                imageId: id,
            })
        ).then(()=> {
            dispatch(
                getCampaignDetails({
                    companyId: company.id,
                    campaignId: campaignId,
                })
            );
        }).catch(error => console.error("Error in Deleting Image for Email Campaign", error));
    };

    const handleCopyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
    };

    const renderEmailCampaignImages = () => {
        return campaign.images?.map((image) => {
            return (
                <Grid item xs={4}>
                    <Container className="flex-row-center " key={'email_campaign_card' + image.id}>
                        <Box my={2}>
                            <Card>
                                <CardActionArea>
                                    <CardMedia
                                        className="height-140px"
                                        image={image.image_url}
                                        title="Email Campaign"
                                    />
                                </CardActionArea>
                                <CardActions className="flex-row-center">
                                    <IconButton 
                                        edge="start" 
                                        onClick={() => handleCopyToClipboard(image.image_url)} 
                                        title='Copy path to clipboard'
                                    >
                                        <ContentPasteIcon />
                                    </IconButton>
                                    <IconButton 
                                        edge="start"  
                                        onClick={() => handleDeleteImage(image.id)}
                                        title='Delete Image'
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </CardActions>
                            </Card>
                        </Box>
                    </Container>
                </Grid>
            );
        });
    };

    const handleFinalizeClick = () => {
        if (isValid && getValues('isChecked') === true) {
            let emailCampaignData = {
                subject: getValues('subject'),
                message: getValues('emailBody'),
            }
            if (campaign.message === null || campaign.message?.id === null) {
                dispatch(createMessage({
                    companyId: company.id,
                    campaignId: campaignId,
                    data: emailCampaignData,
                })).then(async()=> {
                    await handleUpdateCampaign();
                    handleClose();
                    dispatch(getAllCampaigns({ companyId: company.id }));
                }).catch(error => console.error("Error in Creating Message for Email Campaign", error));
            } else {
                dispatch(updateMessage({
                    companyId: company.id,
                    campaignId: campaignId,
                    data: emailCampaignData,
                    messageId: campaign.message.id,
                })).then(async()=> {
                    await handleUpdateCampaign();
                    handleClose();
                    dispatch(getAllCampaigns({ companyId: company.id }));
                }).catch(error => console.error("Error in Updating Message for Email Campaign", error));
            }
        } else {
            setShowModal(!showModal);
        }
    };

    useEffect(() => {
        if (campaignId !== campaign.id) {
            dispatch(
                getCampaignDetails({
                    companyId: company.id,
                    campaignId: campaignId,
                })
            );
        }
    }, []);

    useEffect(() => {
        if (emailBanner) {
            const formData = new FormData();
            formData.append('image', emailBanner);
            dispatch(
                uploadImage({
                    companyId: company.id,
                    campaignId: campaignId,
                    image: formData,
                })
            ).then((result)=> {
                setEmailBanner(null);
            }).catch(error => console.error("Error in Uploading Image for Email Campaign", error));
        }
    }, [emailBanner]);

    useEffect(() => {
        if (campaign && campaign.message) {
            setValue('subject', campaign.message.subject);
            setValue('emailBody', campaign.message.message);
        }
    }, [campaign, campaignId, company]);

    return (
        <>
            <Grid container>
                <Grid item xs={12}>
                    <Box className="padding-24px pt-0">
                        <Box className="pb-12px">
                            <Typography className="text-lg-bold gray-900">
                                Email Campaign
                            </Typography>
                        </Box>
                        <Box className="campaign-message-container pb-12px">
                            <Box className="flex-row-center-start gap-6">
                                <Box className="padding-10px">
                                    <ErrorIcon />
                                </Box>
                                <Typography className="gray-900 text-md-regular">
                                    This is a live campaign, please only upload/add
                                    any content that has been approved by the
                                    Medical, Legal and Regulatory reviewers. Unless
                                    final approval from the reviewers is received,
                                    please do not upload any content.
                                </Typography>
                            </Box>
                        </Box>
                        <Box>
                            <form
                                id="emailCampaign"
                                onSubmit={handleSubmit(onSubmit)}
                            >
                                <Box className="pt-12px pb-12px">
                                    <Typography className="text-sm-medium gray-700">
                                        Subject
                                    </Typography>
                                    <input
                                        className="custom-text-field"
                                        placeholder="Name"
                                        {...register('subject')}
                                    />
                                    <span className="text-xs-regular error-500">
                                        {errors.subject?.message}
                                    </span>
                                </Box>

                                <Box className="pb-12px flex-row gap-4">
                                    <Grid container className="flex-row">
                                        {campaign && campaign.images && campaign.images.length ? renderEmailCampaignImages() : null}
                                    </Grid>
                                </Box>

                                <Box className="pb-12px">
                                    <Box
                                        className="flex-column-center border-1-gray-300 br-16px padding-tb-16-rl-24 wh-100"
                                        {...getRootProps()}
                                    >
                                        <Box className="wh-40px bg-gray-100 br-50 border-6-gray-50">
                                            <Upload />
                                        </Box>
                                        <Box className="flex-row gap-4">
                                            <Typography className="text-sm-medium primary-600">
                                                Click to upload
                                            </Typography>
                                            <Typography className="text-sm-regular gray-500">
                                                or drag and drop
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography className="text-sm-regular gray-500">
                                                PNG, JPG or JPEG
                                            </Typography>
                                        </Box>
                                        <input
                                            {...getInputProps()}
                                            id="emailBannerFileInput"
                                        />

                                        <inputs
                                            type="file"
                                            onChange={handleFileChange}
                                            style={{ display: 'none' }}
                                            id="fileInput"
                                        />
                                    </Box>
                                </Box>

                                <Box className="pb-12px">
                                    <CustomEditor
                                        value={getValues('emailBody')}
                                        onChange={(content) =>
                                            handleEmailBody(content)
                                        }
                                    />
                                </Box>

                                <Box className="flex-row-start-center gap-8">
                                    <Checkbox
                                        classes={{
                                            root: classes.root,
                                            checked: classes.checked,
                                        }}
                                        {...register('isChecked')}
                                    />
                                    <Typography className="text-sm-medium gray-700">
                                        I acknowledge that this campaign has met the
                                        required reviewer approvals
                                    </Typography>
                                </Box>

                                <Box className="pt-12px pb-24px">
                                    <CustomButton
                                        title="Save"
                                        titleColor="#FFFFFF"
                                        buttonColor="#AD509E"
                                        handleClick={handleSave}
                                        type="submit"
                                    />
                                </Box>
                            </form>
                        </Box>

                        {campaign.message && campaign.message.id ? (<Box> 
                            <EmailEchos />
                        </Box>) : null}

                        <Box className="pt-12px pb-24px">
                            <CustomButton
                                title="Finalize Campaign"
                                titleColor="#FFFFFF"
                                buttonColor="#AD509E"
                                handleClick={handleFinalizeClick}
                            />
                        </Box>
                    </Box>
                </Grid>
            </Grid>
            <ConfirmationModal
                open={showModal}
                onClose={()=> setShowModal(!showModal)}
                content={
                    <Box>
                        <Typography className="mb-20px mt-20px" variant="body1">
                            You Need to Acknowledge this Email Campaign
                        </Typography>
                    </Box>
                }
                title={
                    <Typography className="queries-table-title">
                        Acknowledge Campaign
                    </Typography>
                }
                primaryButtonText={'Ok'}
                isCancelShow={false}
                onConfirm={()=> setShowModal(!showModal)}
            />
        </>
    );
}

export default EmailCampaign;
